import {
  BYTES_IN_MEGABYTE,
  MAX_IN_FLIGHT_DOWNLOAD_PROGRESS,
} from "../constants/uploads";
import type {
  IApplyDownloadProgressProps,
  ICreateDownloadProgressReporterProps,
  ICreateDownloadProgressWriterProps,
  IDownloadProgressReporterEvent,
  IProgressTrackedAttachment,
  IWriteDownloadProgressProps,
} from "../types/uploads";

// NOTE: NSAI-5132 — turns a provider→client download's raw progress events into
// the throttled updates the attachment state (and through it the chat chip and
// the Uploads fetch row) is fed with. Shared because the rule is the same on
// both clients and drifting on it means the two surfaces disagree about the
// same download: the provider stream only carries a Content-Length when the
// upstream API reported one, so without a `total` a percentage would be a guess
// and the bytes received are reported instead. Reporting is throttled to a
// whole percent (or a whole megabyte when there is no total) because a large
// video fires hundreds of progress events and each one would otherwise
// re-render every attachment consumer.
export const createDownloadProgressReporter = ({
  onProgress,
}: ICreateDownloadProgressReporterProps): ((
  event: IDownloadProgressReporterEvent,
) => void) => {
  let lastReportedProgress = 0;
  let lastReportedMegabytes = 0;

  return (event: IDownloadProgressReporterEvent): void => {
    if (!event.total) {
      const megabytes = Math.floor(event.loaded / BYTES_IN_MEGABYTE);
      if (megabytes === lastReportedMegabytes) {
        return;
      }
      lastReportedMegabytes = megabytes;
      onProgress({ downloadedBytes: event.loaded });
      return;
    }

    // NOTE: capped below 100 on purpose — 100 is reserved for "all bytes in,
    // now decoding locally", which `progress.preparing` renders.
    const percent = Math.min(
      MAX_IN_FLIGHT_DOWNLOAD_PROGRESS,
      Math.round((event.loaded / event.total) * 100),
    );
    if (percent === lastReportedProgress) {
      return;
    }
    lastReportedProgress = percent;
    onProgress({
      downloadedBytes: event.loaded,
      progress: percent,
      totalBytes: event.total,
    });
  };
};

// NOTE: NSAI-5132 — writes one report onto the attachment it belongs to. Shared
// because both clients hold their downloads in the same shape of array and a
// divergence here is a divergence in what the chat chip and the Uploads fetch
// row show. `progress` is spread in only when it exists so a Content-Length-less
// stream never overwrites the absent percentage with a stuck 0.
export const applyDownloadProgressToAttachments = <
  TAttachment extends IProgressTrackedAttachment,
>({
  attachments,
  id,
  downloadedBytes,
  progress,
  totalBytes,
}: IApplyDownloadProgressProps<TAttachment>): TAttachment[] =>
  attachments.map((attachment) =>
    attachment.id === id
      ? {
          ...attachment,
          downloadedBytes,
          ...(progress !== undefined && { progress }),
          ...(totalBytes !== undefined && { totalBytes }),
        }
      : attachment,
  );
// NOTE: NSAI-5132 — binds that write to a client's attachment state setter, so
// a hook is left with the binding and not with a copy of the update. The setter
// is typed as a plain updater callback rather than React's `Dispatch` to keep
// this package react-free (see CLAUDE.md); a `useState` setter satisfies it.
export const createDownloadProgressWriter = <
  TAttachment extends IProgressTrackedAttachment,
>({
  setAttachments,
}: ICreateDownloadProgressWriterProps<TAttachment>): ((
  props: IWriteDownloadProgressProps,
) => void) => {
  return ({
    id,
    downloadedBytes,
    progress,
    totalBytes,
  }: IWriteDownloadProgressProps): void =>
    setAttachments((currentAttachments) =>
      applyDownloadProgressToAttachments({
        attachments: currentAttachments,
        id,
        downloadedBytes,
        ...(progress !== undefined && { progress }),
        ...(totalBytes !== undefined && { totalBytes }),
      }),
    );
};
